import { useBCP47Locale } from '@portal/shared/hooks/useLocale';
import { CommonSectionProps, useMediaQuery } from '@ag1/ui';
import { TrustpilotWidget as TrustpilotWidgetProps, SanityKeyed } from 'types/studio/studio';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import styles from './TrustpilotWidget.module.scss';

type Props = SanityKeyed<TrustpilotWidgetProps> &
  CommonSectionProps & {
    _key: string;
    width?: string;
    height?: string;
    minHeight?: number;
    className?: string;
  };

const templates = [
  { title: 'Micro Combo', value: '5419b6ffb0d04a076446a9af', minHeight: 20 },
  { title: 'Microstar', value: '5419b732fbfb950b10de65e5', minHeight: 20 },
  { title: 'Mini', value: '53aa8807dec7e10d38f59f32', minHeight: 120 }
];

const extractPxNumber = (str: string): number => {
  if (str.endsWith('px')) {
    const numStr = str.slice(0, -2);
    const num = parseFloat(numStr);
    return Number.isNaN(num) ? 0 : num;
  }
  return 0;
};

export const TrustpilotWidget = (props: Props) => {
  const {
    templateId,
    _key,
    width = '420px',
    height = '20px',
    minHeight,
    position,
    className,
    display
  } = props;
  const isMobile = !useMediaQuery('s');
  const [vw, setVw] = useState(0);

  useEffect(() => {
    const handleResize = () => setVw(window.innerWidth);

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const trustpilot = {
    isEnabled:
      !!templateId &&
      !!process.env.NEXT_PUBLIC_TRUSTPILOT_BUSINESS_UNIT_ID &&
      !!process.env.NEXT_PUBLIC_TRUSTPILOT_DOMAIN &&
      (!display || display === 'both' || (display === 'mobile' ? isMobile : !isMobile)),
    templateId,
    businessUnitId: process.env.NEXT_PUBLIC_TRUSTPILOT_BUSINESS_UNIT_ID,
    domain: process.env.NEXT_PUBLIC_TRUSTPILOT_DOMAIN
  };

  const isoLocale = useBCP47Locale();
  const minimumHeight = minHeight || templates.find((t) => t.value === templateId)?.minHeight || 20;

  return (
    trustpilot.isEnabled && (
      <div
        key={_key}
        className={clsx(
          'trustpilot-widget',
          styles.trustpilot,
          styles[`trustpilot--${position}`],
          styles[`trustpilot--${vw}`],
          className
        )}
        style={{ '--vw': vw } as React.CSSProperties}
        data-locale={isoLocale}
        data-template-id={trustpilot.templateId}
        data-businessunit-id={trustpilot.businessUnitId}
        data-style-height={
          height && extractPxNumber(height) >= minimumHeight ? height : minimumHeight
        }
        data-style-width={vw <= 420 ? '420px' : width}
        data-text-color="#000000">
        <a
          href={`https://uk.trustpilot.com/review/${trustpilot.domain}`}
          target="_blank"
          rel="noopener">
          Trustpilot
        </a>
      </div>
    )
  );
};

export default TrustpilotWidget;
